<template>
  <div>
    <div class="d-flex align-center mb-4">
      <v-btn
        v-if="canCreate"
        color="primary"
        :loading="isCreating"
        @click="createShipment"
      >
        <v-icon left small>mdi-plus</v-icon>
        {{ $t("STOCK.SHIPMENTS.ADD") }}
      </v-btn>
      <span v-else class="caption grey--text">
        {{ $t("STOCK.SHIPMENTS.NO_PERMISSION") }}
      </span>
    </div>

    <v-alert v-if="!items.length" type="info" dense text>
      {{ $t("STOCK.SHIPMENTS.EMPTY") }}
    </v-alert>

    <v-expansion-panels v-else v-model="openPanel" multiple>
      <v-expansion-panel v-for="shipment in items" :key="shipment.id">
        <v-expansion-panel-header>
          <div class="d-flex align-center">
            <strong class="mr-2">
              {{ shipmentTitle(shipment) }}
            </strong>
            <span class="caption grey--text">
              {{ shipment.cmr_document && shipment.cmr_document.cmr_number }}
            </span>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-row class="mb-2">
            <v-col cols="12" md="4">
              <v-text-field
                :value="shipment.label"
                :label="$t('STOCK.SHIPMENTS.LABEL')"
                :disabled="!canEdit"
                @change="updateLabel(shipment, $event)"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                :value="shipment.notes"
                :label="$t('GENERAL.LABELS.NOTES')"
                :disabled="!canEdit"
                @change="updateNotes(shipment, $event)"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="2" class="d-flex align-center">
              <v-btn
                v-if="canEdit"
                color="error"
                outlined
                small
                :loading="deletingId === shipment.id"
                @click="removeShipment(shipment)"
              >
                {{ $t("STOCK.SHIPMENTS.DELETE") }}
              </v-btn>
            </v-col>
          </v-row>

          <div class="shipment-doc-switch mb-4">
            <v-tabs
              v-model="shipment.tab"
              class="shipment-doc-switch__tabs"
              background-color="transparent"
              height="44"
            >
              <v-tab>
                <v-icon left small>mdi-file-document-outline</v-icon>
                {{ $t("STOCK.SHIPMENTS.TAB_CMR") }}
              </v-tab>
              <v-tab>
                <v-icon left small>mdi-file-excel-outline</v-icon>
                {{ $t("STOCK.SHIPMENTS.TAB_TRADE_DOCUMENT") }}
              </v-tab>
            </v-tabs>
          </div>
          <v-tabs-items v-model="shipment.tab">
            <v-tab-item eager>
              <div class="pt-3">
                <v-row class="mb-2">
                  <v-col cols="12" md="4">
                    <v-text-field
                      :value="shipment.cmr_document && shipment.cmr_document.cmr_number"
                      :label="$t('STOCK.CMR.NUMBER')"
                      readonly
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      :value="cmrStatusLabel(shipment.cmr_document && shipment.cmr_document.status)"
                      :label="$t('GENERAL.LABELS.STATUS')"
                      readonly
                    ></v-text-field>
                  </v-col>
                </v-row>
                <div v-if="supportsCmr" class="d-flex align-center flex-wrap mb-3">
                  <v-btn
                    v-if="canEdit"
                    color="primary"
                    outlined
                    small
                    class="mr-2 mb-2"
                    :loading="generatingCmrId === shipment.id"
                    @click="generateCmr(shipment)"
                  >
                    <v-icon left small>mdi-file-word</v-icon>
                    {{ cmrFile(shipment) ? $t("STOCK.CMR.REGENERATE") : $t("STOCK.CMR.GENERATE") }}
                  </v-btn>
                  <v-btn
                    v-if="cmrFile(shipment)"
                    small
                    text
                    class="mb-2"
                    @click="downloadFile(shipment, cmrFile(shipment))"
                  >
                    <v-icon left small>mdi-download</v-icon>
                    {{ $t("GENERAL.LABELS.DOWNLOAD") }}
                  </v-btn>
                </div>
                <v-alert v-else type="info" dense text class="mb-3">
                  {{ $t("STOCK.CMR.NOT_APPLICABLE") }}
                </v-alert>
                <StockCmrForm
                  :form="shipment.cmr_document && shipment.cmr_document.form"
                  :disabled="!canEdit"
                  :saving="savingCmrId === shipment.id"
                  @save="saveCmr(shipment, $event)"
                />
              </div>
            </v-tab-item>
            <v-tab-item eager>
              <div class="pt-3">
                <v-row class="mb-2">
                  <v-col cols="12" md="4">
                    <v-text-field
                      :value="shipment.trade_document && shipment.trade_document.document_number"
                      :label="$t('STOCK.TRADE_DOCUMENT.NUMBER')"
                      readonly
                    ></v-text-field>
                  </v-col>
                </v-row>
                <div class="d-flex align-center flex-wrap mb-3">
                  <v-btn
                    v-if="canEdit"
                    color="primary"
                    outlined
                    small
                    class="mr-2 mb-2"
                    :loading="generatingTradeId === shipment.id"
                    @click="generateTrade(shipment)"
                  >
                    <v-icon left small>mdi-file-excel</v-icon>
                    {{
                      tradeFile(shipment)
                        ? $t("STOCK.TRADE_DOCUMENT.REGENERATE")
                        : $t("STOCK.TRADE_DOCUMENT.GENERATE")
                    }}
                  </v-btn>
                  <v-btn
                    v-if="tradeFile(shipment)"
                    small
                    text
                    class="mb-2"
                    @click="downloadFile(shipment, tradeFile(shipment))"
                  >
                    <v-icon left small>mdi-download</v-icon>
                    {{ $t("GENERAL.LABELS.DOWNLOAD") }}
                  </v-btn>
                </div>
                <StockTradeDocumentForm
                  :form="shipment.trade_document && shipment.trade_document.form"
                  :disabled="!canEdit"
                  :saving="savingTradeId === shipment.id"
                  @save="saveTrade(shipment, $event)"
                />
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import StockCmrForm from "@/views/partials/stock/shipments/StockCmrForm.vue";
import StockTradeDocumentForm from "@/views/partials/stock/shipments/StockTradeDocumentForm.vue";
import {
  CREATE_STOCK_SHIPMENT,
  DELETE_STOCK_SHIPMENT,
  DOWNLOAD_STOCK_SHIPMENT_FILE,
  GENERATE_STOCK_SHIPMENT_CMR,
  GENERATE_STOCK_SHIPMENT_TRADE_DOCUMENT,
  SAVE_STOCK_SHIPMENT_CMR,
  SAVE_STOCK_SHIPMENT_TRADE_DOCUMENT,
  UPDATE_STOCK_SHIPMENT,
} from "@/store/stock/stockShipments.module";
import { SHOW_SNACK } from "@/store/snack.module";

export default {
  name: "StockShipmentsPanel",
  components: { StockCmrForm, StockTradeDocumentForm },
  props: {
    transportOrderId: {
      type: Number,
      required: true,
    },
    shipments: {
      type: Array,
      default: () => [],
    },
    canCreate: {
      type: Boolean,
      default: false,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    supportsCmr: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      items: [],
      openPanel: [],
      isCreating: false,
      deletingId: null,
      savingCmrId: null,
      savingTradeId: null,
      generatingCmrId: null,
      generatingTradeId: null,
    };
  },
  watch: {
    shipments: {
      immediate: true,
      handler(value) {
        if (value === this.items) {
          return;
        }
        const tabsById = {};
        this.items.forEach((item) => {
          tabsById[item.id] = item.tab;
        });
        this.items = (value || []).map((shipment) => ({
          ...shipment,
          tab: tabsById[shipment.id] != null ? tabsById[shipment.id] : shipment.tab || 0,
        }));
      },
    },
  },
  methods: {
    shipmentTitle(shipment) {
      const no = shipment.shipment_no;
      const label = shipment.label ? ` — ${shipment.label}` : "";
      return this.$t("STOCK.SHIPMENTS.TITLE_ITEM", { no }) + label;
    },
    cmrStatusLabel(status) {
      const key = `STOCK.STATUSES.CMR.${(status || "").toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : status || "-";
    },
    cmrFile(shipment) {
      return (
        (shipment.cmr_document &&
          (shipment.cmr_document.generated_file || shipment.cmr_document.draft_pdf_file)) ||
        null
      );
    },
    tradeFile(shipment) {
      return (shipment.trade_document && shipment.trade_document.generated_file) || null;
    },
    replaceShipment(updated) {
      const index = this.items.findIndex((item) => item.id === updated.id);
      if (index === -1) {
        this.items.push({ ...updated, tab: 0 });
        return;
      }
      this.$set(this.items, index, {
        ...updated,
        tab: this.items[index].tab || 0,
      });
      this.$emit("updated", this.items);
    },
    async createShipment() {
      this.isCreating = true;
      try {
        const created = await this.$store.dispatch(CREATE_STOCK_SHIPMENT, {
          transportOrderId: this.transportOrderId,
        });
        this.items.push({ ...created, tab: 0 });
        this.openPanel = [...this.openPanel, this.items.length - 1];
        this.$emit("updated", this.items);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          color: "success",
        });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isCreating = false;
      }
    },
    async updateLabel(shipment, label) {
      await this.patchShipment(shipment, { label });
    },
    async updateNotes(shipment, notes) {
      await this.patchShipment(shipment, { notes });
    },
    async patchShipment(shipment, params) {
      if (!this.canEdit) {
        return;
      }
      try {
        const updated = await this.$store.dispatch(UPDATE_STOCK_SHIPMENT, {
          id: shipment.id,
          params,
        });
        this.replaceShipment(updated);
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      }
    },
    async removeShipment(shipment) {
      if (!window.confirm(this.$t("STOCK.SHIPMENTS.CONFIRM_DELETE"))) {
        return;
      }
      this.deletingId = shipment.id;
      try {
        await this.$store.dispatch(DELETE_STOCK_SHIPMENT, shipment.id);
        this.items = this.items.filter((item) => item.id !== shipment.id);
        this.$emit("updated", this.items);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          color: "success",
        });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.deletingId = null;
      }
    },
    async saveCmr(shipment, form) {
      this.savingCmrId = shipment.id;
      try {
        const updated = await this.$store.dispatch(SAVE_STOCK_SHIPMENT_CMR, {
          id: shipment.id,
          params: { form },
        });
        this.replaceShipment(updated);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          color: "success",
        });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.savingCmrId = null;
      }
    },
    async saveTrade(shipment, form) {
      this.savingTradeId = shipment.id;
      try {
        const updated = await this.$store.dispatch(SAVE_STOCK_SHIPMENT_TRADE_DOCUMENT, {
          id: shipment.id,
          params: { form },
        });
        this.replaceShipment(updated);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          color: "success",
        });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.savingTradeId = null;
      }
    },
    async generateCmr(shipment) {
      this.generatingCmrId = shipment.id;
      try {
        const updated = await this.$store.dispatch(GENERATE_STOCK_SHIPMENT_CMR, shipment.id);
        this.replaceShipment(updated);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          color: "success",
        });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: this.getErrorMessage(errors),
          color: "error",
        });
      } finally {
        this.generatingCmrId = null;
      }
    },
    async generateTrade(shipment) {
      this.generatingTradeId = shipment.id;
      try {
        const updated = await this.$store.dispatch(
          GENERATE_STOCK_SHIPMENT_TRADE_DOCUMENT,
          shipment.id
        );
        this.replaceShipment(updated);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          color: "success",
        });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: this.getErrorMessage(errors),
          color: "error",
        });
      } finally {
        this.generatingTradeId = null;
      }
    },
    downloadFile(shipment, file) {
      if (!file || !file.id) {
        return;
      }
      this.$store
        .dispatch(DOWNLOAD_STOCK_SHIPMENT_FILE, {
          id: shipment.id,
          fileId: file.id,
          originalName: file.original_name,
          name: file.name,
        })
        .catch((errors) => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(errors),
            color: "error",
          });
        });
    },
    getErrorMessage(errors) {
      if (!errors) {
        return this.$t("GENERAL.UNEXPECTED_ERROR");
      }
      if (typeof errors === "string") {
        return errors;
      }
      if (errors.message) {
        return errors.message;
      }
      if (errors.errors) {
        const firstKey = Object.keys(errors.errors)[0];
        if (firstKey && errors.errors[firstKey][0]) {
          return errors.errors[firstKey][0];
        }
      }
      return this.$t("GENERAL.UNEXPECTED_ERROR");
    },
  },
};
</script>

<style scoped>
.shipment-doc-switch {
  display: inline-flex;
  max-width: 100%;
  padding: 4px;
  background: #e8f1ef;
  border: 1px solid #c5d8d4;
  border-radius: 12px;
}

.shipment-doc-switch__tabs {
  max-width: 100%;
}

.shipment-doc-switch__tabs ::v-deep .v-tabs-bar {
  height: 40px;
  background: transparent !important;
}

.shipment-doc-switch__tabs ::v-deep .v-tabs-slider-wrapper {
  display: none;
}

.shipment-doc-switch__tabs ::v-deep .v-tab {
  min-width: 180px;
  margin: 0 2px;
  padding: 0 16px;
  border-radius: 8px;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
  color: #5b6b68;
}

.shipment-doc-switch__tabs ::v-deep .v-tab:hover {
  background: rgba(255, 255, 255, 0.55);
}

.shipment-doc-switch__tabs ::v-deep .v-tab--active {
  background: #fff;
  color: #1d7f72;
  box-shadow: 0 1px 4px rgba(29, 127, 114, 0.18);
}

.shipment-doc-switch__tabs ::v-deep .v-icon {
  color: inherit;
}
</style>
